import React from 'react';
import { Route, Routes } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

import NavBar from '@/components/layout/NavBar/NavBar';
import Footer from '@/components/layout/Footer/Footer';
import ProtectedRoutes from '@/components/common/ProtectedRoutes';
import PublicRoutes from '@/components/common/PublicRoutes';
import AuthRestrictedRoutes from '@/components/common/AuthRestrictedRoutes';

import HomePage from '@/features/home/HomePage';
import NotFoundPage from '@/features/home/NotFoundPage';
import GuidelinesPage from '@/features/guidelines/GuidelinesPage';
import PrivacyPage from '@/features/policies/PrivacyPage';
import TermsPage from '@/features/policies/TermsPage';
import AccessibilityPage from '@/features/policies/AccessibilityPage';
import AdminPage from '@/features/admin/AdminPage';
import BrowsePage from '@/features/listings/BrowsePage';
import ListingFormPage from '@/features/listings/ListingFormPage';
import MyListingsPage from '@/features/listings/MyListingsPage';
import ListingDetailPage from '@/features/listings/ListingDetailPage';
import MyRequestsPage from '@/features/requests/MyRequestsPage';
import RequestThreadPage from '@/features/requests/RequestThreadPage';
import LogIn from '@/features/auth/components/LogIn';
import SignUp from '@/features/auth/components/SignUp';
import ForgotPassword from '@/features/auth/components/ForgotPassword';
import CreateProfile from '@/features/profile/components/CreateProfile';
import ProfilePage from '@/features/profile/components/ProfilePage';
import EditProfile from '@/features/profile/components/EditProfile';

function App() {
    return (
        <div className="App">
            <a href="#App-main" className="afe-skip-link">
                Skip to main content
            </a>
            <header id="App-header">
                <NavBar />
            </header>
            {/* The skip link above moves focus here, and a browser moves
                focus to a fragment target only when that target can hold
                it. Without tabIndex the link scrolls the page and leaves
                focus where it was, so the next Tab returns to the top of
                the navigation: the journey the link exists to skip. */}
            <main id="App-main" tabIndex={-1}>
                <Routes>
                    <Route
                        path="/"
                        element={
                            <PublicRoutes>
                                <HomePage />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/login"
                        element={
                            <AuthRestrictedRoutes>
                                <LogIn />
                            </AuthRestrictedRoutes>
                        }
                    />
                    <Route
                        path="/signup"
                        element={
                            <AuthRestrictedRoutes>
                                <SignUp />
                            </AuthRestrictedRoutes>
                        }
                    />
                    <Route
                        path="/forgot-password"
                        element={
                            <AuthRestrictedRoutes>
                                <ForgotPassword />
                            </AuthRestrictedRoutes>
                        }
                    />
                    {/* /listings/new and /listings/mine come before
                        /listings/:id, which would otherwise match them and
                        try to load a listing called "new". */}
                    <Route
                        path="/listings"
                        element={
                            <ProtectedRoutes>
                                <BrowsePage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/listings/new"
                        element={
                            <ProtectedRoutes>
                                <ListingFormPage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/listings/mine"
                        element={
                            <ProtectedRoutes>
                                <MyListingsPage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/listings/:id"
                        element={
                            <ProtectedRoutes>
                                <ListingDetailPage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/listings/:id/edit"
                        element={
                            <ProtectedRoutes>
                                <ListingFormPage />
                            </ProtectedRoutes>
                        }
                    />
                    {/* /requests/mine comes before /requests/:id for the
                        same reason /listings/mine does: the parameter route
                        would otherwise match it and look for a request
                        called "mine". */}
                    <Route
                        path="/requests/mine"
                        element={
                            <ProtectedRoutes>
                                <MyRequestsPage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/requests/:id"
                        element={
                            <ProtectedRoutes>
                                <RequestThreadPage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/create-profile"
                        element={
                            <ProtectedRoutes>
                                <CreateProfile />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoutes>
                                <ProfilePage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/profile/edit"
                        element={
                            <ProtectedRoutes>
                                <EditProfile />
                            </ProtectedRoutes>
                        }
                    />
                    {/* Public: the footer links here from signed-out pages,
                        and the API serves every section without a token. */}
                    <Route path="/guidelines" element={<GuidelinesPage />} />
                    {/* Public for the same reason, and because someone
                        deciding whether to sign up reads these first. */}
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route
                        path="/accessibility"
                        element={<AccessibilityPage />}
                    />
                    {/* The admin area routes its own tabs below /admin,
                        so the whole subtree is gated once here. Coordinators
                        enter too; AdminPage decides which tabs each role
                        sees, and the server scopes every call. */}
                    <Route
                        path="/admin/*"
                        element={
                            <ProtectedRoutes
                                requiredRole={['admin', 'coordinator']}
                            >
                                <AdminPage />
                            </ProtectedRoutes>
                        }
                    />
                    {/* Last: an unmatched address renders the not-found page
                        rather than an empty main. */}
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </main>
            {/* A div, not a footer: Footer renders the page's one
                contentinfo landmark itself, and a footer around it nests a
                second one, which screen readers announce twice. */}
            <div id="App-footer">
                <Footer />
            </div>
        </div>
    );
}

export default App;
